/*
 * COPYRIGHT (c) 2023
 * HONEYWELL INC.,
 * ALL RIGHTS RESERVED
 *
 * This software is a copyrighted work and/or information protected as a trade secret.
 * Legal rights of Honeywell Inc. in this software is distinct from ownership of any medium
 * in which the software is embodied. Copyright or trade secret notices included must be
 * reproduced in any copies authorized by Honeywell Inc.
 *
 */

import { Icon, Popup, VerticalMenu } from '@scuf/common';
import React from 'react';
import {
  IAny,
  ISummaryTableDataObject
} from '../../../DashboardKPIWidgets/IEnergyManagementProps';
import { ISummaryTableColumn } from './SummaryTable';
import { useTranslation } from '@scuf/localization';
import descriptors from '../../../../locales/en-US/descriptors';
import { formatDate } from '../../../DetailKPIWidgets/UtilityBillViewDetailsComponent/UtilityBillViewDetailsUtils';

interface ICSVDownloadProps {
  data: ISummaryTableDataObject[];
  columns: ISummaryTableColumn[];
  csvFileName: string;
  costCurrency?: string;
}
const CSVDownloadComponent = (props: ICSVDownloadProps) => {
  const { data, columns, csvFileName } = props;
  const [t] = useTranslation();
  const d = descriptors.energyManagement;

  const handleDownload = () => {
    const csv = convertToCSV(data);
    const blob = new Blob([csv], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `${csvFileName}.csv`;
    link.click();
    URL.revokeObjectURL(url);
  };

  const scaleIndividualCost = (cost: number) => {
    let scaledCost = Number(cost);
    if (scaledCost === 0) {
      return scaledCost;
    }
    if (scaledCost < 1000) {
      return scaledCost === Math.floor(scaledCost)
        ? scaledCost
        : scaledCost.toFixed(2);
    } else if (scaledCost >= 1000 && scaledCost < 1000000) {
      return (scaledCost / 1000).toFixed(2) + 'K';
    } else if (scaledCost >= 1000000 && scaledCost < 1000000000) {
      return (scaledCost / 1000000).toFixed(2) + 'M';
    }
  };

  const convertToCSV = (data: any[]) => {
    data = data.map((item) => {
      const billingCycle = `${formatDate(
        item.billingCycleStartDate
      )} - ${formatDate(item.billingCycleEndDate)} (${
        item.billingCycleNumberOfDays
      } days)`;
      return { ...item, billingCycle };
    });
    let header = columns.map((column: ISummaryTableColumn) => {
      return column.unit
        ? column.costField
          ? `${column.header} (${props.costCurrency})`
          : `${column.header} (${column.unit})`
        : `${column.header}`;
    });
    header.pop();
    const csv = [
      header.join(','),
      ...data.map((row) => {
        return header
          .map((field, index) => {
            if (
              [
                'totalcost',
                't&dcost',
                'others',
                'consumptioncharge',
                'servicecharge',
                'consumptioncost',
                'consumptioncharges',
                'demandcost',
                'demandcharge',
                'demandcharges',
                'servicecost',
                'servicecharges'
              ].includes(columns[index].field)
            ) {
              return scaleIndividualCost(
                row.dynamicColumnData[
                  row.dynamicColumnData.findIndex(
                    (a: IAny) => a.key === columns[index].field
                  )
                ].value
              );
            }
            return JSON.stringify(row[columns[index].field]);
          })
          .join(',');
      })
    ].join('\r\n');
    return csv;
  };

  return (
    <Popup
      element={<Icon root="common" name="cloud-download" size="medium" />}
      hoverable={false}
      position="bottom right"
      on="click"
    >
      <VerticalMenu>
        <VerticalMenu.Item onClick={handleDownload}>
          {t(d.DOWNLOAD_CSV)}
        </VerticalMenu.Item>
      </VerticalMenu>
    </Popup>
  );
};

export default CSVDownloadComponent;
